/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type Session } from '../../lib/api/sessions.js';
import {
  useClientLate,
  useConsultantAbsent,
  useConsultantCheckIn,
  useTransitionSession,
} from '../../lib/queries/sessions.js';

interface Props {
  readonly session: Session;
  readonly onClose: () => void;
}

const TEXTAREA_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

/** Admin records a session outcome (BRD v2.0 CR-005), incl. the consultant-absent 30% flow. */
export function SessionOutcomeModal({ session, onClose }: Props): JSX.Element {
  const { t } = useTranslation();
  const transition = useTransitionSession();
  const clientLate = useClientLate();
  const checkIn = useConsultantCheckIn();
  const absent = useConsultantAbsent();
  const [reason, setReason] = useState('');
  const checkedIn = session.consultantCheckedInAt !== null;
  const busy =
    transition.isPending || clientLate.isPending || checkIn.isPending || absent.isPending;
  const failed = transition.isError || clientLate.isError || checkIn.isError || absent.isError;

  const complete = useCallback(() => {
    transition.mutate({ id: session.id, action: 'complete' }, { onSuccess: onClose });
  }, [transition, session.id, onClose]);
  const noShow = useCallback(() => {
    transition.mutate({ id: session.id, action: 'no-show' }, { onSuccess: onClose });
  }, [transition, session.id, onClose]);
  const late = useCallback(() => {
    clientLate.mutate({ id: session.id }, { onSuccess: onClose });
  }, [clientLate, session.id, onClose]);
  const doCheckIn = useCallback(() => {
    checkIn.mutate({ id: session.id });
  }, [checkIn, session.id]);
  const onReason = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
  }, []);
  const markAbsent = useCallback(() => {
    absent.mutate({ id: session.id, reason }, { onSuccess: onClose });
  }, [absent, session.id, reason, onClose]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Button type="button" onClick={complete} disabled={busy}>
          {t('admin.sessions.outcomeComplete')}
        </Button>
        <Button type="button" variant="outline" onClick={late} disabled={busy}>
          {t('admin.sessions.outcomeClientLate')}
        </Button>
        <Button type="button" variant="outline" onClick={noShow} disabled={busy}>
          {t('admin.sessions.outcomeNoShow')}
        </Button>
      </div>

      <div className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {t('admin.sessions.consultantAbsentTitle')}
        </p>
        {checkedIn ? (
          <>
            <label htmlFor="absent-reason" className="block text-xs text-slate-500">
              {t('admin.sessions.absentReason')}
            </label>
            <textarea
              id="absent-reason"
              rows={2}
              className={TEXTAREA_CLASS}
              value={reason}
              onChange={onReason}
            />
            <Button type="button" onClick={markAbsent} disabled={busy || reason.trim() === ''}>
              {t('admin.sessions.markClientAbsent')}
            </Button>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500">{t('admin.sessions.checkInHint')}</p>
            <Button type="button" variant="outline" onClick={doCheckIn} disabled={busy}>
              {t('admin.sessions.markConsultantArrived')}
            </Button>
          </>
        )}
      </div>

      {failed && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
    </div>
  );
}
